import React, { useContext, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { Switch, Input } from 'syngenta-digital-cropwise-react-ui-kit';
import useDebounce from 'hooks/useDebounce';
import useSubmissionAnalytics from 'screens/Integrations/hooks/useSubmissionAnalytics';
import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import Editor from 'screens/Property/PropertiesLanding/components/Editor';
import CropSeasonSingleSelectionDropdown from 'components/CropSeasonSingleSelectionDropdown';
import SubmissionFooter from 'screens/Integrations/components/SubmissionFooter';
import CloseIcon from 'assets/close_18px.svg?react';
import SearchIcon from 'assets/search.svg';
import DateRangePicker from 'components/LandingSearch/components/DateRangePicker';
import { customDateFormat } from 'helpers/dateFormat';
import CwFPagination from 'components/CwFPagination';
import useFormikHandler from 'screens/Integrations/hooks/useFormikHandler';
import useIncomingInvoiceApi from 'screens/Integrations/hooks/useIncomingInvoiceApi';
import events from 'utilities/amplitude/events';
import { toastManager } from 'components/ToastContainer/toastManager';
import { AgVendContext } from '../context/Provider';
import AgVendInvoiceTable from './components/AgVendInvoicesTable';

import './index.css';

const AgVendInvoiceList = () => {
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [renderDateRangePicker, setRenderDateRangePicker] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(20);
  const [includePreviousImported, setIncludePreviousImported] = useState(false);
  const [filteredInvoices, setFilteredInvoices] = useState([]);

  const { triggerAnalyticsEvent } = useSubmissionAnalytics();
  const { goForward, releaseStepForward } = useContext(AgVendContext);

  const { loading: isLoading, listIncomingInvoices } = useIncomingInvoiceApi();

  const { values, setFieldValue } = useFormikContext();
  const {
    selectedCropSeasons,
    setSelectedCropSeasons,
    selectedInvoices,
    setSelectedInvoices,
    setExcludedProductsIds
  } = useFormikHandler();

  const timeDelay = 600;
  const debouncedSearchTerm = useDebounce(searchText, timeDelay);

  useEffect(() => {
    setExcludedProductsIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function listInvoices() {
      if (selectedCropSeasons?.length > 0) {
        triggerAnalyticsEvent(
          events.epic.Integrations.IncomingInvoice.invoiceList,
          {
            vendor: INTEGRATIONS.agVend
          }
        );
        const response = await listIncomingInvoices({
          vendor: INTEGRATIONS.agVend.toLowerCase(),
          displayImported: includePreviousImported,
          searchText: debouncedSearchTerm,
          page: currentPage - 1,
          limit: pageSize,
          ...(dateRange?.length === 2
            ? {
                startDate: customDateFormat(
                  dateRange[0].toISOString(),
                  'yyyy-MM-dd'
                ),
                endDate: customDateFormat(
                  dateRange[1].toISOString(),
                  'yyyy-MM-dd'
                )
              }
            : {})
        });
        if (response) {
          releaseStepForward();
          setFieldValue('invoices', response.data);
        }
      }
    }
    listInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCropSeasons,
    includePreviousImported,
    debouncedSearchTerm,
    dateRange,
    currentPage
  ]);

  useEffect(() => {
    if (values.invoices) {
      const { data: responseData, elements } = values.invoices;

      setTotalElements(elements);
      setFilteredInvoices(responseData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.invoices]);

  const handleSelectInvoice = invoice => {
    if (!selectedInvoices?.length > 0) {
      return setSelectedInvoices([invoice]);
    }

    const notMatchInvoices = selectedInvoices?.filter(
      ({ id }) => id !== invoice.id
    );

    if (notMatchInvoices.length !== selectedInvoices?.length) {
      return setSelectedInvoices(notMatchInvoices);
    }

    return setSelectedInvoices([invoice].concat(selectedInvoices));
  };

  const handleCropSeasonSelect = seasonSelected => {
    setSelectedCropSeasons([seasonSelected]);
  };

  const onNextHandler = async () => {
    if (selectedInvoices?.length > 20) {
      toastManager.warn('You can only select up to 20 invoices');
      return;
    }
    triggerAnalyticsEvent(
      events.epic.Integrations.IncomingInvoice.invoiceSelect,
      {
        vendor: INTEGRATIONS.agVend
      }
    );
    goForward();
  };

  const handleFilter = searchValue => {
    setSearchText(searchValue);
    setSelectedInvoices([]);
    setCurrentPage(1);
  };

  const onDateRangeChange = event => {
    if (event?.target?.value?.length !== 2) return;
    setDateRange(event.target.value);
    setCurrentPage(1);
    setSelectedInvoices([]);
  };

  const clearDateRange = () => {
    setDateRange([]);
    setSelectedInvoices([]);
    setCurrentPage(1);
    setRenderDateRangePicker(false);
  };

  useEffect(() => {
    // Force re-render of DateRangePicker since is not possible to cleanup the values with the current component implementation
    if (!renderDateRangePicker) {
      setRenderDateRangePicker(true);
    }
  }, [renderDateRangePicker]);

  return (
    <>
      <div className="w-full mx-6">
        <div
          className="flex flex-col bg-white top-30 left-26 px-6 py-4 rounded-lg shadow-sm-card border-1"
          data-testid="agvend-invoice-selection"
        >
          <div className="flex flex-col">
            <div className="text-xl leading-7 font-semibold text-neutral-1000 mb-2">
              Select Invoices
            </div>
            <p className="text-base leading-6 text-neutral-70">
              Select the Cropwise crop season you would like to import into and
              your retailer supplied invoices.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-2 text-sm">
            <Editor label="Cropwise Financials Crop Season*" halfWidth>
              <div className="text-sm">
                <CropSeasonSingleSelectionDropdown
                  id="cwfCropSeason"
                  value={selectedCropSeasons ? selectedCropSeasons[0] : null}
                  onChange={value => {
                    handleCropSeasonSelect(value);
                  }}
                  createNewCropSeasonEnabled
                />
              </div>
            </Editor>
          </div>
        </div>
        <div className="flex flex-row mt-8 mb-1 items-center">
          <div className="flex flex-row">
            <div className="mr-4 relative inline-block min-w-332px base-drop-down-search-input">
              <div className="prefix-icon">
                <img alt="Search icon" src={SearchIcon} />
              </div>
              <Input
                id="label-search-input"
                data-testid="search-input"
                placeholder="Search by invoice #, retailer, or products"
                allowClear
                disabled={isLoading}
                type="default"
                autoComplete="off"
                onChange={e => handleFilter(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-row items-center justify-center">
            {renderDateRangePicker ? (
              <DateRangePicker onDateRangeChange={onDateRangeChange} />
            ) : (
              <div className="relative w-64 border mr-4 bg-white p-1 rounded-md">
                &nbsp;
              </div>
            )}
            {dateRange?.length === 2 && (
              <button
                className="syt-antd-input-clear-icon clear-date-range"
                alt="clear date range"
                data-testid="clear-date-range"
                aria-label="clear date range"
                title="clear date range"
                type="button"
                disabled={isLoading}
                tabIndex="-1"
                onClick={clearDateRange}
              >
                <CloseIcon />
              </button>
            )}
          </div>
          <div className="my-auto">
            <Switch
              size="medium"
              checked={includePreviousImported}
              disabled={isLoading}
              onChange={newValue => {
                setIncludePreviousImported(newValue);
                setSelectedInvoices([]);
                setCurrentPage(1);
              }}
            />
            <span
              className={`ml-2 ${!includePreviousImported && 'text-gray-500'} `}
            >
              Display previously imported invoices
            </span>
          </div>
          <div className="w-40 ml-auto text-right">
            <span
              className={`${
                selectedInvoices?.length > 0 ? 'text-blue-50' : 'text-gray-500'
              } font-bold`}
            >
              {`${selectedInvoices?.length ?? 0} ${
                selectedInvoices?.length > 1 ? 'records' : 'record'
              } selected`}
            </span>
          </div>
        </div>
        <AgVendInvoiceTable
          filteredInvoices={filteredInvoices}
          handleSelection={handleSelectInvoice}
          selectedInvoices={selectedInvoices}
          isLoading={isLoading}
          isCropSeasonSelected={selectedCropSeasons?.length > 0}
          PaginationComponent={() =>
            CwFPagination(
              totalElements,
              currentPage - 1,
              page => setCurrentPage(page + 1),
              null,
              pageSize
            )
          }
        />
      </div>
      <SubmissionFooter
        nextDisabled={!selectedInvoices?.length || !selectedCropSeasons?.length}
        onNext={onNextHandler}
        isLastStep={false}
        context={AgVendContext}
        nextLabel="Next: Match Companies"
        integrationType={INTEGRATIONS.agVend}
        isBottomFixed
      />
    </>
  );
};

export default AgVendInvoiceList;
